import { c as _c } from "react-compiler-runtime";
import { useEffect } from 'react';
import { useCellSelectionDispatch } from '../../redux/reduxHooks';
import { useCellSelectionScopeId } from '../context/CellSelectionContext';
import { clearCellSelection } from '../redux/cellSelectionSlice';
import { CELL_SELECTABLE_ATTRIBUTE } from '../../table/constants/cellSelectionAttributes';
import { isEventFromOverlay } from '../utils/isEventFromOverlay';

/**
 * Clears the cell selection on interactions outside the selectable cells:
 *   - pressing Escape, and
 *   - a mousedown anywhere that isn't inside a selectable cell (checkboxes,
 *     headers, blank space, other UI).
 *
 * Escape is staged so it unwinds one layer of state at a time. When the event
 * comes from a cell that is in edit mode (`data-editing="true"`) or from inside
 * an overlay (a portalled popover such as a date picker), the keypress belongs
 * to that inner UI — the consumer's editor or the overlay closes itself and the
 * selection is left intact. A later Escape, with nothing editing, clears it.
 *
 * Only called from CellSelectionEffects, which is mounted only when cell
 * selection is enabled. The document mousedown listener runs after React's
 * synthetic per-cell handler (bubble order), so clicking a real cell selects
 * it and then this listener sees the cell and skips clearing.
 *
 * Scope-aware: skips clearing only when the clicked cell belongs to *this*
 * table's scope, so nested tables (e.g. an inline association table) each
 * maintain independent selections without bleeding into each other.
 *
 * Mousedowns inside overlays (floating-ui portals, dialogs, dropdowns) are
 * skipped too: dispatching there would re-render subscribed cells synchronously
 * during mousedown, stealing focus from the overlay's inputs before the browser
 * delivers it.
 */
export const useClearCellSelectionOnExternalInteraction = () => {
  const $ = _c(4);
  const dispatch = useCellSelectionDispatch();
  const scopeId = useCellSelectionScopeId();
  let t0;
  let t1;
  if ($[0] !== dispatch || $[1] !== scopeId) {
    t0 = () => {
      const handleKeyDown = e => {
        if (e.key !== "Escape") {
          return;
        }
        const {
          target
        } = e;
        if (target instanceof Element && target.closest("[data-editing=\"true\"]")) {
          return;
        }
        if (isEventFromOverlay(target)) {
          return;
        }
        dispatch(clearCellSelection());
      };
      const handleMouseDown = e_0 => {
        const {
          target: target_0
        } = e_0;
        if (isEventFromOverlay(target_0)) {
          return;
        }
        if (target_0 instanceof Element) {
          const selectable = target_0.closest(`[${CELL_SELECTABLE_ATTRIBUTE}]`);
          if ((selectable === null || selectable === void 0 ? void 0 : selectable.getAttribute(CELL_SELECTABLE_ATTRIBUTE)) === scopeId) {
            return;
          }
        }
        dispatch(clearCellSelection());
      };
      document.addEventListener("keydown", handleKeyDown);
      document.addEventListener("mousedown", handleMouseDown);
      return () => {
        document.removeEventListener("keydown", handleKeyDown);
        document.removeEventListener("mousedown", handleMouseDown);
      };
    };
    t1 = [dispatch, scopeId];
    $[0] = dispatch;
    $[1] = scopeId;
    $[2] = t0;
    $[3] = t1;
  } else {
    t0 = $[2];
    t1 = $[3];
  }
  useEffect(t0, t1);
};